import { z } from 'zod'

import {
  fiveSegmentDisplayColumnKeys,
  fiveSegmentSortColumnKeys,
  type NineSegmentColumnKey as NineSegmentColumnKeyBase,
  nineSegmentDisplayColumnKeys as nineSegmentDisplayColumnKeysBase,
  nineSegmentSortColumnKeys as nineSegmentSortColumnKeysBase,
} from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import { dataDisplayTypes, segmentDisplayTypes } from '~/utils'

import {
  type RestoringParams,
  type RestoringParamsAddition,
  type RestoringParamsOrigin,
} from '../types'

type NineSegmentColumnKey = NineSegmentColumnKeyBase | 'aNpi'
const nineSegmentSortColumnKeys = [
  ...nineSegmentSortColumnKeysBase,
  'aNpi',
] as const satisfies readonly NineSegmentColumnKey[]
const nineSegmentDisplayColumnKeys = [
  ...nineSegmentDisplayColumnKeysBase,
  'aNpi',
] as const satisfies readonly NineSegmentColumnKey[]

const schema2Origin = z
  .object({
    version: z.literal(2),
    revision: z.number(),
    brandGroupId: z.number(),
    dataDisplayType: z.enum(dataDisplayTypes),
    segmentDisplayType: z.enum(segmentDisplayTypes),
    fiveSegmentSortValue: z
      .object({
        columnKey: z.enum(fiveSegmentSortColumnKeys),
        ascending: z.boolean(),
      })
      .nullable(),
    nineSegmentSortValue: z
      .object({
        columnKey: z.enum(nineSegmentSortColumnKeys),
        ascending: z.boolean(),
      })
      .nullable(),
    hiddenRowIds: z.array(z.union([z.number(), z.enum(['average', 'max'])])),
    fiveSegmentHiddenColumnKeys: z.array(z.enum(fiveSegmentDisplayColumnKeys)),
    nineSegmentHiddenColumnKeys: z.array(z.enum(nineSegmentDisplayColumnKeys)),
    hash: z.enum(['5segs', '9segs']),
  })
  .transform<RestoringParamsOrigin>((value) => ({
    ...value,
    nineSegmentSortValue:
      value.nineSegmentSortValue?.columnKey === 'aNpi'
        ? null
        : (value.nineSegmentSortValue as RestoringParamsOrigin['nineSegmentSortValue']),
    nineSegmentHiddenColumnKeys: value.nineSegmentHiddenColumnKeys.filter(
      (columnKey) => columnKey !== 'aNpi',
    ) as RestoringParamsOrigin['nineSegmentHiddenColumnKeys'],
  }))

const schema2Addition = z.object({
  fiveSegmentComment: z.string(),
  nineSegmentComment: z.string(),
}) satisfies z.ZodSchema<RestoringParamsAddition>

export const schema2 = schema2Origin.and(
  schema2Addition.partial(),
) as z.ZodType<RestoringParams>
